/**
 * Dialog gestione registro — trasmissione bozze e export/archivio.
 * Tab Movimenti: invio batch bozze per intervallo progressivo/anno.
 * Tab Export e archivio: XML, JSON, MUD CSV, archivio cloud, conservazione MARTEWEB e DIP.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Archive, Download, RefreshCw, Send } from "lucide-react";
import { endpoints, type DraftItem } from "@/api/client";
import { ConservazioneStato } from "@/components/ConservazioneStato";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type TabId = "movimenti" | "pagine";

/** Ogni verifica interroga l'API MarteWeb: solo le copie più recenti. */
const CONSERVATION_CHECK_LIMIT = 5;

function payloadObject(p: unknown): Record<string, unknown> | null {
  if (p && typeof p === "object" && !Array.isArray(p))
    return p as Record<string, unknown>;
  return null;
}

function draftProgressivo(d: DraftItem): number | null {
  const p = payloadObject(d.payload);
  if (!p) return null;
  const n = Number(p.progressivo);
  if (Number.isFinite(n)) return n;
  const rif = payloadObject(p.riferimenti);
  const nr = rif ? payloadObject(rif.numero_registrazione) : null;
  const fromNr = nr ? Number(nr.progressivo) : NaN;
  return Number.isFinite(fromNr) ? fromNr : null;
}

function draftAnno(d: DraftItem): number {
  const p = payloadObject(d.payload);
  const fallbackYear = () => new Date().getFullYear();
  if (!p) return fallbackYear();
  const a = Number(p.anno);
  if (Number.isFinite(a)) return a;
  const data = String(p.data ?? "");
  const y = parseInt(data.slice(0, 4), 10);
  if (Number.isFinite(y)) return y;
  const rif = payloadObject(p.riferimenti);
  const nr = rif ? payloadObject(rif.numero_registrazione) : null;
  const yNr = nr ? Number(nr.anno) : NaN;
  if (Number.isFinite(yNr)) return yNr;
  const dor = rif ? String(rif.data_ora_registrazione ?? "").slice(0, 10) : "";
  const yDor = parseInt(dor.slice(0, 4), 10);
  return Number.isFinite(yDor) ? yDor : fallbackYear();
}

export function InviaRegistroDialog({
  registroId,
  open,
  onOpenChange,
}: {
  registroId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabId>("movimenti");
  const [anno, setAnno] = useState(() => String(new Date().getFullYear()));
  const [da, setDa] = useState("1");
  const [a, setA] = useState("999");
  const [mudAnno, setMudAnno] = useState(() => String(new Date().getFullYear()));

  const allDraftsQ = useQuery({
    queryKey: ["drafts", "movement-all", registroId],
    queryFn: () => endpoints.drafts.list({ doc_type: "movement" }),
    enabled: open && !!registroId,
  });

  const archivesQ = useQuery({
    queryKey: ["archive", "registro", registroId],
    queryFn: () =>
      endpoints.archive.list({
        doc_kind: "registro_xml",
        reference_id: registroId,
        limit: 20,
      }),
    enabled: open && !!registroId,
  });

  const archiveMut = useMutation({
    mutationFn: () => endpoints.archive.snapshotRegistro(registroId),
    onSuccess: () => {
      toast.success("Registro archiviato su cloud");
      qc.invalidateQueries({ queryKey: ["archive", "registro", registroId] });
    },
    onError: (e) => {
      toast.error("Archivio non riuscito", {
        description: e instanceof Error ? e.message : String(e),
      });
    },
  });

  const pending = useMemo(() => {
    const items = (allDraftsQ.data ?? []).filter(
      (d) =>
        d.parent_id === registroId &&
        (d.status === "draft" || d.status === "ready"),
    );
    const annoN = parseInt(anno, 10);
    const daN = parseInt(da, 10);
    const aN = parseInt(a, 10);
    return items.filter((d) => {
      const prog = draftProgressivo(d);
      const y = draftAnno(d);
      if (!Number.isFinite(annoN)) return true;
      if (y !== annoN) return false;
      if (prog == null) return true;
      return prog >= daN && prog <= aN;
    });
  }, [allDraftsQ.data, registroId, anno, da, a]);

  const sendMut = useMutation({
    mutationFn: async () => {
      const results: { id: string; status: number }[] = [];
      for (const d of pending) {
        const sent = await endpoints.drafts.send(d.id);
        results.push({ id: d.id, status: sent.rentri_status });
      }
      return results;
    },
    onSuccess: (results) => {
      const ok = results.filter((r) => r.status < 400).length;
      toast.success(`Invio completato: ${ok}/${results.length} bozze`, {
        description: results.some((r) => r.status === 202)
          ? "Alcune transazioni sono in elaborazione (Job RENTRI)."
          : undefined,
      });
      qc.invalidateQueries({ queryKey: ["drafts"] });
      qc.invalidateQueries({ queryKey: ["movimenti", registroId] });
      qc.invalidateQueries({ queryKey: ["movimenti-count", registroId] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
    onError: (e) => {
      toast.error("Errore invio", {
        description: e instanceof Error ? e.message : String(e),
      });
    },
  });

  const downloadJson = async () => {
    try {
      const data = await endpoints.registri.get(registroId);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `registro-${registroId}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download non riuscito", {
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Gestione registro</DialogTitle>
          <p className="text-xs text-muted-foreground font-mono">{registroId}</p>
          <DialogDescription className="text-xs">
            Bozze movimenti collegate al registro: filtro per anno e intervallo di
            numerazione progressiva (formato lista o payload RENTRI normalizzato).
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b pb-2">
          <TabBtn active={tab === "movimenti"} onClick={() => setTab("movimenti")}>
            Movimenti
          </TabBtn>
          <TabBtn active={tab === "pagine"} onClick={() => setTab("pagine")}>
            Export e archivio
          </TabBtn>
        </div>

        {tab === "movimenti" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Seleziona l&apos;intervallo di movimenti (bozze locali) da trasmettere a
              RENTRI.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Anno">
                <Input
                  value={anno}
                  onChange={(e) => setAnno(e.target.value)}
                  className="font-mono"
                />
              </Field>
              <Field label="Da n.">
                <Input
                  value={da}
                  onChange={(e) => setDa(e.target.value)}
                  className="font-mono"
                />
              </Field>
              <Field label="A n.">
                <Input
                  value={a}
                  onChange={(e) => setA(e.target.value)}
                  className="font-mono"
                />
              </Field>
            </div>
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <span className="font-medium">{pending.length}</span> bozze da inviare
              {allDraftsQ.isLoading && (
                <span className="text-muted-foreground"> (caricamento…)</span>
              )}
            </div>
            {pending.length > 0 && (
              <ul className="max-h-32 overflow-y-auto text-xs space-y-1">
                {pending.map((d) => (
                  <li key={d.id} className="flex justify-between gap-2">
                    <span className="truncate">{d.title ?? d.id.slice(0, 8)}</span>
                    <Badge variant="outline">{d.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "pagine" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Scarica copia del registro o report MUD. L&apos;archivio cloud salva
              l&apos;XML completo (vidimazione + movimenti) e lo invia in
              conservazione su MARTEWEB; a conservazione completata puoi scaricare
              il DIP.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                className="justify-start"
                disabled={archiveMut.isPending}
                onClick={() => archiveMut.mutate()}
              >
                <Archive className="mr-2 h-4 w-4" />
                {archiveMut.isPending ? "Archiviazione…" : "Archivia registro su cloud"}
              </Button>
              {(archivesQ.data ?? []).length > 0 && (
                <ul className="rounded-md border bg-muted/20 p-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                  {(archivesQ.data ?? []).map((a, idx) => (
                    <li key={a.id} className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 flex-wrap items-center gap-1">
                        <span className="truncate">
                          {a.created_at?.slice(0, 19).replace("T", " ") ?? a.id.slice(0, 8)}
                        </span>
                        <Badge
                          variant={
                            a.status === "archived"
                              ? "success"
                              : a.status === "failed"
                                ? "destructive"
                                : "outline"
                          }
                          className="text-[10px] px-1 py-0"
                        >
                          {a.status}
                        </Badge>
                        {idx < CONSERVATION_CHECK_LIMIT && (
                          <ConservazioneStato doc={a} compact />
                        )}
                      </span>
                      {a.status === "archived" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2"
                          onClick={async () => {
                            try {
                              const name = `${registroId}_registro.xml`;
                              await endpoints.archive.downloadFile(a.id, name);
                            } catch (e) {
                              toast.error("Download non riuscito", {
                                description:
                                  e instanceof Error ? e.message : String(e),
                              });
                            }
                          }}
                        >
                          XML
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <Button variant="outline" className="justify-start" onClick={downloadJson}>
                <Download className="mr-2 h-4 w-4" /> JSON registro
              </Button>
              <Button
                variant="outline"
                className="justify-start"
                onClick={() =>
                  endpoints.mud.downloadCsv(registroId, parseInt(mudAnno, 10))
                }
              >
                <Download className="mr-2 h-4 w-4" /> MUD CSV ({mudAnno})
              </Button>
              <Field label="Anno MUD">
                <Input
                  value={mudAnno}
                  onChange={(e) => setMudAnno(e.target.value)}
                  className="font-mono w-28"
                />
              </Field>
              <Button
                variant="ghost"
                size="sm"
                className="justify-start"
                onClick={() => {
                  qc.invalidateQueries({ queryKey: ["movimenti", registroId] });
                  toast.success("Lista movimenti aggiornata");
                }}
              >
                <RefreshCw className="mr-2 h-4 w-4" /> Aggiorna esiti invio
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
          {tab === "movimenti" && (
            <Button
              onClick={() => sendMut.mutate()}
              disabled={sendMut.isPending || pending.length === 0}
            >
              <Send className="mr-2 h-4 w-4" />
              {sendMut.isPending ? "Invio…" : "Invia a RENTRI"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
